import { Button } from "@/components";
import { useDesignMaxTheme } from "@/packages";
import { useNewNavigate, usePensionNameValue } from "@/shared/client";
import { type RoomPreviewInfoResponse } from "@/shared/server";
import { FONT } from "@/styles";
import { Box, Center, chakra, Flex, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

export const RoomInfo = ({
    currentIdx,
    nextSlide,
    prevSlide,
    data,
}: {
    currentIdx: number;
    nextSlide: () => void;
    prevSlide: () => void;
    data: RoomPreviewInfoResponse;
}) => {
    const pensionName = usePensionNameValue();
    const { onNavigate } = useNewNavigate();
    const { ref, inView } = useInView({ threshold: 0.9 });
    const { MEDIAS, COLORS } = useDesignMaxTheme();
    const { isMobile, isLaptop, isDesktop } = MEDIAS();
    const pensionKrNameFontSize = isLaptop ? 16 : 20;
    const pensionEngNameFontSize = isLaptop ? 32 : 48;
    const roomNamePCFontSize = isLaptop ? 24 : 32;

    const infoBoxPCWidth = isLaptop ? "528px" : "664px";

    const btnPCHeight = isLaptop ? "48px" : "54px !important";

    useEffect(() => {
        let intervalId: number | null = null;

        if (inView) {
            const timeoutId = window.setTimeout(() => {
                intervalId = window.setInterval(() => {
                    nextSlide();
                }, 3000);
            }, 900);

            return () => {
                clearTimeout(timeoutId);
                if (intervalId !== null) {
                    clearInterval(intervalId);
                }
            };
        }
        return () => {};
    }, [inView]);

    return (
        <>
            <Box>
                <Text {...FONT("notoSerif", isMobile ? 14 : pensionKrNameFontSize, 500)}>{pensionName}의 객실</Text>
                <Text {...FONT("cormorant", isMobile ? 20 : pensionEngNameFontSize, 700, 1.2)}>ROOMS</Text>
            </Box>
            <Box ref={ref} position="relative" flex="1">
                {data.currentRooms.map((room, index) => (
                    <MotionRoomDetail
                        key={room.roomIdx}
                        position="absolute"
                        w="100%"
                        h="100%"
                        display="flex"
                        flexDir={isMobile ? "column" : "row"}
                        rowGap={isMobile ? "16px" : "0"}
                        columnGap={isLaptop ? "24px" : "48px"}
                        initial={{ opacity: index === currentIdx ? 1 : 0 }}
                        animate={{
                            opacity: index === currentIdx ? 1 : 0,
                            transition: { duration: 1, ease: "easeOut" },
                        }}
                    >
                        <Flex
                            columnGap="24px"
                            justifyContent="space-between"
                            maxWidth={isDesktop ? "848px" : "1008px"}
                            flex={isMobile ? "1" : "auto"}
                            w="100%"
                            sx={{
                                "& div": {
                                    borderRadius: "16px",
                                },
                            }}
                        >
                            <Box
                                width={isLaptop ? "100%" : "50%"}
                                height="100%"
                                background={`url(${room.imgList[1].imgUrl})no-repeat center/cover`}
                            ></Box>
                            {!isLaptop && (
                                <Box
                                    width="50%"
                                    height="100%"
                                    background={`url(${room.imgList[2].imgUrl})no-repeat center/cover`}
                                ></Box>
                            )}
                        </Flex>
                        <Flex
                            maxW={isMobile ? "100%" : infoBoxPCWidth}
                            w="100%"
                            flexDir="column"
                            justifyContent="space-between"
                        >
                            <Flex
                                maxW={isMobile ? "100%" : infoBoxPCWidth}
                                w="100%"
                                maxH={isMobile ? "152px" : "none"}
                                h={isMobile ? "100%" : "auto"}
                                flexDir="column"
                                rowGap={isMobile ? "16px" : "24px"}
                                sx={{
                                    "& > button": {
                                        h: isMobile ? "40px" : btnPCHeight,
                                        paddingY: "15px",
                                    },
                                }}
                            >
                                <Flex
                                    flexDir="column"
                                    rowGap="16px"
                                    maxH={isMobile ? "96px" : "none"}
                                    h={isMobile ? "100%" : "auto"}
                                >
                                    <Text {...FONT("notoSerif", isMobile ? 16 : roomNamePCFontSize, 500)}>
                                        {room.roomDetailInfo.name}
                                    </Text>
                                    <Box
                                        sx={{
                                            "& p": {
                                                color: COLORS.gray[500],
                                            },
                                        }}
                                        h={isMobile ? "64px" : "auto"}
                                    >
                                        <Text {...FONT("pretendard", isMobile ? 14 : 16, 300)}>
                                            #기준{room.roomDetailInfo.personOfCapacity.min}인 #최대
                                            {room.roomDetailInfo.personOfCapacity.max}인
                                        </Text>
                                        <Text {...FONT("pretendard", isMobile ? 14 : 16, 300)}>
                                            {room.roomDetailInfo.structure}
                                        </Text>
                                    </Box>
                                </Flex>
                                <Button
                                    w={isLaptop ? "128px" : "164px"}
                                    onClick={() => onNavigate({ path: `/rooms/${room.roomIdx}` })}
                                >
                                    자세히보기
                                </Button>
                            </Flex>
                            <Flex justifyContent="flex-end" alignItems="center" columnGap="24px">
                                <Text className="material-icons-outlined" onClick={prevSlide} cursor="pointer">
                                    arrow_back_ios
                                </Text>

                                <Flex alignItems="center" columnGap="8px" w="88px">
                                    <Center w="40px">
                                        <Text {...FONT("pretendard", isLaptop ? 24 : 36, 700)}>{currentIdx + 1}</Text>
                                    </Center>
                                    <Flex flex="1" textAlign="center" columnGap="8px">
                                        <Text {...FONT("pretendard", 16, 500)}>/</Text>
                                        <Text {...FONT("pretendard", 16, 500)}>{data.currentRooms.length}</Text>
                                    </Flex>
                                </Flex>

                                <Text className="material-icons-outlined" onClick={nextSlide} cursor="pointer">
                                    arrow_forward_ios
                                </Text>
                            </Flex>
                        </Flex>
                    </MotionRoomDetail>
                ))}
            </Box>
        </>
    );
};

const MotionRoomDetail = chakra(motion.div);
